/**
 * RunOptionsBadges — says what a stored profiling run's score is a score of.
 *
 * A run can be started with the rule-based DQ passes, key detection or the relationship map switched off. Its score
 * and issue count are then computed over fewer checks, so wherever a run's score is shown (run history, the project
 * home, the summary) a small plain badge names what was off, with a tooltip. The score itself is never hidden.
 *
 * The backend sends the options on every run payload (`run_options`); a missing value (an older backend, or a run
 * recorded before the options were stored) means a full run and renders nothing.
 */

import type { RunOptions } from "../../api/profiler-api";

export interface RunOptionNote {
  key: "dq" | "keys" | "relationships";
  label: string;
  title: string;
}

/** The badges a run needs, in a fixed order; empty for a full run. Only an explicit `false` counts as off. */
export function runOptionNotes(
  options: Partial<RunOptions> | null | undefined,
): RunOptionNote[] {
  const notes: RunOptionNote[] = [];
  if (!options) return notes;
  if (options.dq === false) {
    notes.push({
      key: "dq",
      label: "Rules off: statistics-only score",
      title:
        "Data quality rules were switched off for this run. The score reflects the baseline column statistics only, not the engagement rule packs or business-insight checks, so it is not directly comparable with a full run.",
    });
  }
  if (options.keys === false) {
    notes.push({
      key: "keys",
      label: "Keys off: no relationships",
      title:
        "Key detection was switched off for this run. No primary or foreign keys were found, so there are no orphan-reference checks and no relationship map, and the score is not directly comparable with a full run.",
    });
  } else if (options.relationships === false) {
    notes.push({
      key: "relationships",
      label: "Relationship map off",
      title:
        "The relationship map was switched off for this run. Keys were still detected and the score is unaffected.",
    });
  }
  return notes;
}

export function RunOptionsBadges({
  options,
  className = "",
}: {
  options: Partial<RunOptions> | null | undefined;
  className?: string;
}) {
  const notes = runOptionNotes(options);
  if (notes.length === 0) return null;
  return (
    <div
      className={`flex flex-wrap gap-1 ${className}`}
      data-testid="run-options-badges"
    >
      {notes.map((n) => (
        <span
          key={n.key}
          title={n.title}
          data-run-option={n.key}
          className="text-[11px] font-normal leading-tight px-1.5 py-0.5 rounded border bg-amber-400/10 text-amber-200 border-amber-400/30"
        >
          {n.label}
        </span>
      ))}
    </div>
  );
}
